import { useEffect, useRef } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { formatInr, formatNumber } from '../utils/formatters';

const metricDefinitions = [
  ['totalVariableCost', 'Variable cost', 'currency'],
  ['allocatedOverhead', 'Allocated overhead', 'currency'],
  ['totalTreatmentCost', 'Total treatment cost', 'currency'],
  ['grossProfit', 'Gross profit', 'currency'],
  ['netContribution', 'Net contribution', 'currency'],
  ['profitMarginPercent', 'Margin against break-even', 'percent'],
  ['breakEvenPrice', 'Break-even price', 'currency'],
  ['priceGap', 'Price gap', 'currency'],
  ['additionalMonthlyCases', 'Additional monthly cases', 'number'],
  ['additionalDailyCases', 'Additional daily cases', 'number'],
  ['revenuePerTreatmentMinute', 'Revenue per minute', 'currency'],
  ['costPerTreatmentMinute', 'Cost per minute', 'currency'],
  ['chairTimeProfitability', 'Chair-time contribution/min', 'currency'],
  ['currentUtilizationPercent', 'Chair utilisation', 'percent'],
];

function formatMetric(value, type) {
  if (value === null || value === undefined) return '—';
  if (type === 'currency') return formatInr(value);
  if (type === 'percent') return `${formatNumber(value, { maximumFractionDigits: 2 })}%`;
  return formatNumber(value, { maximumFractionDigits: 2 });
}

export default function TreatmentAnalysisModal({
  open,
  treatment,
  analysis,
  loading,
  error,
  onClose,
  returnFocusRef,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusRef?.current?.focus();
    };
  }, [onClose, open, returnFocusRef]);

  if (!open) return null;
  const metrics = analysis?.metrics || {};
  const missingInputs = analysis?.missingInputs || [];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0c1f3d]/65 p-3 backdrop-blur-sm sm:p-6">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="treatment-analysis-title"
        tabIndex="-1"
        className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 bg-gradient-to-r from-[#0c1f3d] to-[#0d9488] px-5 py-5 text-white sm:px-7">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-100">Treatment profitability</p>
            <h2 id="treatment-analysis-title" className="mt-2 text-xl font-semibold sm:text-2xl">
              {treatment?.name || 'Treatment analysis'}
            </h2>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close treatment analysis" className="rounded-full border border-white/30 bg-white/10 p-2 text-white hover:bg-white/20">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-7">
          {loading ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-[#0d9488]" />
              <p className="mt-3 text-sm font-semibold">Analysing this treatment…</p>
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-800">
              <p className="font-semibold">Analysis unavailable</p>
              <p className="mt-1 text-sm">{error}</p>
            </div>
          ) : !analysis ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">No analysis data is available.</div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl bg-[#ecfdf7] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-teal-700">Current price</p>
                  <p className="mt-2 text-xl font-bold text-[#0c1f3d]">{formatInr(metrics.currentPrice)}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Estimated cost</p>
                  <p className="mt-2 text-xl font-bold text-[#0c1f3d]">{formatInr(metrics.totalTreatmentCost)}</p>
                </div>
                <div className={`rounded-2xl p-4 ${Number(metrics.netContribution) >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Profit / loss</p>
                  <p className={`mt-2 text-xl font-bold ${Number(metrics.netContribution) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatInr(metrics.netContribution)}</p>
                </div>
                <div className="rounded-2xl bg-[#0c1f3d] p-4 text-white">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">Status</p>
                  <p className="mt-2 text-lg font-bold">{analysis.statusLabel}</p>
                </div>
              </div>

              {missingInputs.length > 0 ? (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                  <h3 className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-5 w-5" /> Complete these fields</h3>
                  <p className="mt-2 text-sm text-amber-800">Profitability advice is withheld until the required inputs are complete.</p>
                  <ul className="mt-3 space-y-2 text-sm text-amber-900">
                    {missingInputs.map((item) => <li key={`${item.field}-${item.location}`}>• <b>{item.field}</b> — {item.location}</li>)}
                  </ul>
                </section>
              ) : (
                <>
                  <section>
                    <h3 className="text-lg font-semibold text-[#0c1f3d]">Key metrics</h3>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {metricDefinitions.filter(([key]) => metrics[key] !== undefined).map(([key, label, type]) => (
                        <div key={key} className="rounded-2xl border border-slate-200 p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
                          <p className="mt-2 font-bold text-slate-900">{formatMetric(metrics[key], type)}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section className="grid gap-5 lg:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 p-5">
                      <h3 className="font-semibold text-[#0c1f3d]">What the numbers indicate</h3>
                      <ul className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                        {(analysis.narrative || []).map((line, index) => <li key={`${index}-${line.slice(0, 20)}`}>• {line}</li>)}
                      </ul>
                    </div>
                    <div className="rounded-2xl border border-teal-100 bg-[#ecfdf7] p-5">
                      <h3 className="font-semibold text-[#0c1f3d]">Recommended actions</h3>
                      <ol className="mt-3 space-y-3 text-sm leading-6 text-slate-700">
                        {(analysis.recommendations || []).map((line, index) => <li key={`${index}-${line.slice(0, 20)}`}><b>{index + 1}.</b> {line}</li>)}
                      </ol>
                    </div>
                  </section>
                </>
              )}
            </div>
          )}
        </div>

        <footer className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-7">
          <button type="button" onClick={onClose} className="rounded-full bg-[#0c1f3d] px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Close</button>
        </footer>
      </div>
    </div>
  );
}
